import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faCalendarDays,
  faCircleInfo,
  faSpinnerThird,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import {
  BicButtonComponent,
  BicButtonGroupComponent,
  BicButtonGroupItemComponent,
  BicDatepickerInputDirective,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicToastService,
  BicToggleComponent,
} from '@buildinginformationcloud/bic-ui-components';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ProjectControllerService} from '../../../../api';
import {UserSimpleDto} from '../../../../api/model/userSimpleDto';
import {CheckRunScheduleView} from '../../../../api/model/checkRunScheduleView';
import {CheckRunScheduleRequest} from '../../../../api/model/checkRunScheduleRequest';
import {SchedulePreviewView} from '../../../../api/model/schedulePreviewView';
import {CheckRunScheduleApi} from '../../../../services/check-run-schedule/check-run-schedule.api';
import {
  formatBerlin,
  INTERVAL_OPTIONS,
  isoToLocalDate,
  MIN_CRON_INTERVAL_MINUTES,
  parseDateDe,
  PATTERN_OPTIONS,
  removedRecipientsHint,
  retainMemberRecipients,
  ScheduleFormValue,
  SchedulePatternValue,
  ScheduleWeekdayValue,
  toIsoDate,
  todayBerlinIso,
  toScheduleRequest,
  validateScheduleForm,
  WEEKDAY_OPTIONS,
} from '../../../../services/check-run-schedule/check-run-schedule.format';
import {switchMapAfterQuiet} from '../../../../services/check-run-schedule/check-run-schedule.preview';
import {StateConfigService} from '../../../../services/state/state-config.service';
import {serverErrorMessage} from '../../shared/server-error-message';

/** Ein wählbarer Prüflauf im Popup (nur beim Anlegen aus der Übersicht). */
export interface ScheduleCheckOption {
  id: string;
  name: string;
  /**
   * Builder-Schritt, in dem die beim Start abgefragte Auswahl festgelegt wird (1 = Regeln,
   * 2 = Dateien, 0 = anderer Schritt). `null` = nichts wird abgefragt; `undefined` = unbekannt.
   */
  startSelectionStep?: number | null;
  /** Darf im Builder geöffnet werden (nicht zentral bereitgestellt, keine Freigabe). */
  editable: boolean;
}

/** Ziel von „Auswahl im Prüflauf festlegen“. */
export interface ScheduleConfigureRequest {
  definitionId: string;
  /** Builder-Schritt, der geöffnet werden soll. */
  step: number;
}

type PreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; preview: SchedulePreviewView }
  | { kind: 'error'; message: string };

/**
 * Zeitplan für einen Prüflauf anlegen oder bearbeiten.
 *
 * <p>Die Eingaben werden laufend (entprellt) an `POST …/preview` geschickt. Die Vorschau zeigt die
 * nächsten Termine und – maßgeblich – die Hinderungsgründe des Backends, etwa eine manuelle Regel-
 * oder Dateiauswahl im Prüflauf. Solange es solche Gründe gibt, ist „Speichern“ gesperrt.
 *
 * <p>Fehler beim Speichern (409/422) stehen im Popup selbst; der globale Fehler-Toast ist für diese
 * Aufrufe abgeschaltet (siehe {@link CheckRunScheduleApi}).
 */
@Component({
  selector: 'app-check-run-schedule-popup',
  standalone: true,
  imports: [
    FormsModule,
    FaIconComponent,
    BicPopupComponent,
    BicButtonComponent,
    BicButtonGroupComponent,
    BicButtonGroupItemComponent,
    BicSelectComponent,
    BicDropdownElementComponent,
    BicInputComponent,
    BicDatepickerInputDirective,
    BicToggleComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './check-run-schedule-popup.component.html',
})
export class CheckRunSchedulePopupComponent implements OnInit, OnDestroy {
  @Input({required: true}) projectId!: number;
  /** Bestehender Zeitplan (Bearbeiten) oder `null` (Anlegen). */
  @Input() schedule: CheckRunScheduleView | null = null;
  /** Fester Prüflauf, z. B. aus Karte oder Builder. */
  @Input() definitionId?: string;
  @Input() definitionName?: string;
  /** Wählbare Prüfläufe, wenn kein fester vorgegeben ist (Anlegen aus der Übersicht). */
  @Input() checkOptions: ScheduleCheckOption[] = [];

  @Output() saved = new EventEmitter<CheckRunScheduleView>();
  @Output() deleted = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();
  /** „Auswahl im Prüflauf festlegen“: der Aufrufer navigiert in den Builder. */
  @Output() configureDefinition = new EventEmitter<ScheduleConfigureRequest>();

  protected readonly faCalendarDays = faCalendarDays;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faTriangleExclamation = faTriangleExclamation;

  protected readonly patternOptions = PATTERN_OPTIONS;
  protected readonly weekdayOptions = WEEKDAY_OPTIONS;
  protected readonly intervalOptions = INTERVAL_OPTIONS;
  protected readonly minCronInterval = MIN_CRON_INTERVAL_MINUTES;

  protected form!: ScheduleFormValue;
  /**
   * Was die Datumsfelder anzeigen, als `ngModel`: ein gültiges Datum als `Date`, ein unfertig
   * getippter Text als der Text selbst. Der Text gleicht dem Wert, den das Feld schon gemeldet hat,
   * und `ngModel` schreibt ihn deshalb nicht zurück. (Seit bic-ui-components 1.0.57 zeigt das Feld
   * auch `[value]` an und formatiert ISO- und deutsche Strings als `TT.MM.JJJJ`; der `Date` ist also
   * keine Bedingung der Bibliothek mehr.)
   */
  protected startDateModel: Date | string | null = null;
  protected endDateModel: Date | string | null = null;
  /** Ergebnis der clientseitigen Vorprüfung (Feld, kein Getter). */
  protected formIssues: string[] = [];
  protected preview: PreviewState = {kind: 'idle'};
  /** „15.10.2026, 12:00 Uhr · 22.10.2026, 12:00 Uhr“ aus der Vorschau. */
  protected nextRunsText = '';

  protected memberOptions: { id: number; label: string }[] = [];
  protected membersLoading = true;
  protected membersError = false;
  /** Hinweis, dass Empfänger ohne Projektmitgliedschaft entfernt wurden (Feld, kein Getter). */
  protected removedRecipientsHint: string | null = null;

  protected saving = false;
  protected deleting = false;
  protected confirmDelete = false;
  protected serverError: string | null = null;
  /**
   * Gewählter Prüflauf aus {@link checkOptions}, wenn er beim Start eine Auswahl abfragt; sonst
   * `null` (Feld, kein Getter).
   */
  protected interactiveOption: ScheduleCheckOption | null = null;

  /** Muster vor dem Einschalten des Expertenmodus; beim Ausschalten wird es wiederhergestellt. */
  private patternBeforeExpert: SchedulePatternValue = 'DAILY';

  private readonly api = inject(CheckRunScheduleApi);
  private readonly projectApi = inject(ProjectControllerService);
  private readonly stateConfigService = inject(StateConfigService);
  private readonly toastService = inject(BicToastService);
  private readonly previewTrigger = new Subject<CheckRunScheduleRequest>();
  private previewSubscription?: Subscription;

  protected get isEdit(): boolean {
    return this.schedule != null;
  }

  /** Prüflauf steht fest (Bearbeiten, Karte, Builder) – dann gibt es keine Auswahl. */
  protected get fixedDefinitionName(): string | null {
    if (this.schedule) return this.schedule.definitionName ?? 'Prüflauf nicht mehr vorhanden';
    if (this.definitionId) return this.definitionName ?? '';
    return null;
  }

  /** Expertenmodus: der Zeitplan ist ein Cron-Ausdruck. */
  protected get expertMode(): boolean {
    return this.form.pattern === 'CRON';
  }

  protected get notifyEnabled(): boolean {
    return this.form.notifyOnFailure || this.form.notifyOnSuccess;
  }

  protected get previewIssues(): string[] {
    return this.preview.kind === 'ready' ? this.preview.preview.issues ?? [] : [];
  }

  protected get canSave(): boolean {
    return !this.saving
      && !this.deleting
      && this.formIssues.length === 0
      && this.preview.kind === 'ready'
      && (this.preview.preview.issues ?? []).length === 0;
  }

  ngOnInit(): void {
    this.form = this.initialForm();
    this.startDateModel = isoToLocalDate(this.form.startDate);
    this.endDateModel = isoToLocalDate(this.form.endDate);
    this.updateInteractiveOption();

    // Jede Änderung bricht eine laufende Vorschau sofort ab, nicht erst nach der Wartezeit.
    this.previewSubscription = this.previewTrigger.pipe(
      switchMapAfterQuiet(300, request => this.loadPreview(request)),
    ).subscribe(state => this.applyPreview(state));

    this.loadMembers();
    this.onFormChanged();
  }

  ngOnDestroy(): void {
    this.previewSubscription?.unsubscribe();
  }

  protected setDefinition(id: string): void {
    this.patchForm({definitionId: id || null});
    this.updateInteractiveOption();
  }

  /** Öffnet den Prüflauf im Builder, um die Auswahl dort fest zu hinterlegen. */
  protected configureSelection(): void {
    const option = this.interactiveOption;
    if (!option || !option.editable || this.saving || this.deleting) return;
    this.configureDefinition.emit({definitionId: option.id, step: option.startSelectionStep || 1});
  }

  protected setExpertMode(enabled: boolean): void {
    if (enabled === this.expertMode) return;
    if (enabled) {
      this.patternBeforeExpert = this.form.pattern;
      this.patchForm({pattern: 'CRON'});
    } else {
      this.setPattern(this.patternBeforeExpert);
    }
  }

  protected setCron(text: string): void {
    this.patchForm({cronExpression: text ?? ''});
  }

  protected setPattern(value: unknown): void {
    const pattern = value as SchedulePatternValue;
    const patch: Partial<ScheduleFormValue> = {pattern};
    // Bei „Einmalig“ verschwindet das Enddatum-Feld. Ein unfertig getippter Text wird dabei verworfen,
    // beim Zurückwechseln ist das Feld leer; ein gültiges Enddatum bleibt erhalten.
    if (pattern === 'ONCE' && this.form.endDate && !isoToLocalDate(this.form.endDate)) {
      patch.endDate = null;
      this.endDateModel = null;
    }
    // Beim ersten Wechsel auf wöchentlich den Wochentag des Startdatums vorschlagen.
    if (pattern === 'WEEKLY' && this.form.weekdays.length === 0) {
      patch.weekdays = [this.weekdayOf(this.form.startDate)];
    }
    this.patchForm(patch);
  }

  protected setWeekdays(value: unknown): void {
    const weekdays = Array.isArray(value) ? value as ScheduleWeekdayValue[] : [];
    this.patchForm({weekdays: [...weekdays]});
  }

  protected setInterval(value: unknown): void {
    this.patchForm({intervalWeeks: Number(value) || 1});
  }

  /** Freitext; normalisiert wird erst beim Senden (siehe `toScheduleRequest`). */
  protected setTime(text: string): void {
    this.patchForm({timeOfDay: text ?? ''});
  }

  protected onStartDatePicked(date: Date): void {
    const iso = toIsoDate(date);
    this.startDateModel = isoToLocalDate(iso);
    if (iso !== this.form.startDate) this.patchForm({startDate: iso});
  }

  /** Text im Feld: getippt, oder ein erneut gewähltes Datum, das `bicDatepickerInput` beim Schließen meldet. */
  protected onStartDateText(text: string): void {
    const iso = parseDateDe(text);
    this.startDateModel = iso ? isoToLocalDate(iso) : text;
    const startDate = iso ?? '';
    if (startDate !== this.form.startDate) this.patchForm({startDate});
  }

  protected onEndDatePicked(date: Date): void {
    const iso = toIsoDate(date);
    this.endDateModel = isoToLocalDate(iso);
    if (iso !== this.form.endDate) this.patchForm({endDate: iso});
  }

  /** Wie {@link onStartDateText}; ein leeres Feld heißt „ohne Enddatum“. */
  protected onEndDateText(text: string): void {
    const iso = parseDateDe(text);
    this.endDateModel = iso ? isoToLocalDate(iso) : text;
    const endDate = text.trim() ? iso ?? text.trim() : null;
    if (endDate !== this.form.endDate) this.patchForm({endDate});
  }

  protected clearEndDate(): void {
    this.endDateModel = null;
    this.patchForm({endDate: null});
  }

  protected setNotifyOnFailure(value: boolean): void {
    this.patchForm({notifyOnFailure: value});
  }

  protected setNotifyOnSuccess(value: boolean): void {
    this.patchForm({notifyOnSuccess: value});
  }

  protected setMembers(value: unknown): void {
    const ids = Array.isArray(value) ? value.map(v => Number(v)).filter(v => !Number.isNaN(v)) : [];
    this.patchForm({notifyUserIds: ids});
  }

  protected setEmails(text: string): void {
    this.patchForm({notifyEmailsText: text ?? ''});
  }

  protected save(): void {
    if (!this.canSave) return;
    this.saving = true;
    this.serverError = null;
    const request = toScheduleRequest(this.form) as CheckRunScheduleRequest;
    const save$ = this.schedule
      ? this.api.update(this.projectId, this.schedule.id, request)
      : this.api.create(this.projectId, request);
    save$.subscribe({
      next: view => {
        this.saving = false;
        this.toastService.success('Zeitplan gespeichert');
        this.saved.emit(view);
      },
      error: err => {
        this.saving = false;
        this.serverError = serverErrorMessage(err, 'Der Zeitplan konnte nicht gespeichert werden.');
      },
    });
  }

  /** Erster Klick fragt nach, der zweite löscht. */
  protected requestDelete(): void {
    if (!this.schedule || this.deleting) return;
    if (!this.confirmDelete) {
      this.confirmDelete = true;
      return;
    }
    const scheduleId = this.schedule.id;
    this.deleting = true;
    this.serverError = null;
    this.api.delete(this.projectId, scheduleId).subscribe({
      next: () => {
        this.deleting = false;
        this.toastService.success('Zeitplan gelöscht');
        this.deleted.emit(scheduleId);
      },
      error: err => {
        this.deleting = false;
        this.confirmDelete = false;
        this.serverError = serverErrorMessage(err, 'Der Zeitplan konnte nicht gelöscht werden.');
      },
    });
  }

  protected close(): void {
    if (this.saving || this.deleting) return;
    this.closed.emit();
  }

  private initialForm(): ScheduleFormValue {
    const schedule = this.schedule;
    if (schedule) {
      return {
        definitionId: schedule.definitionId,
        pattern: schedule.pattern as SchedulePatternValue,
        weekdays: [...(schedule.weekdays ?? [])] as ScheduleWeekdayValue[],
        intervalWeeks: schedule.intervalWeeks ?? 1,
        // Bei CRON ohne Uhrzeit; der Vorschlag gilt erst, wenn jemand den Expertenmodus verlässt.
        timeOfDay: schedule.timeOfDay ?? '08:00',
        cronExpression: schedule.cronExpression ?? '',
        startDate: schedule.startDate,
        endDate: schedule.endDate ?? null,
        notifyOnFailure: !!schedule.notifyOnFailure,
        notifyOnSuccess: !!schedule.notifyOnSuccess,
        notifyUserIds: [...(schedule.notifyUserIds ?? [])],
        notifyEmailsText: (schedule.notifyEmails ?? []).join(', '),
      };
    }
    const currentUserId = this.stateConfigService.user?.id;
    return {
      definitionId: this.definitionId ?? null,
      pattern: 'DAILY',
      weekdays: [],
      intervalWeeks: 1,
      timeOfDay: '08:00',
      cronExpression: '',
      startDate: todayBerlinIso(),
      endDate: null,
      notifyOnFailure: true,
      notifyOnSuccess: false,
      notifyUserIds: currentUserId != null ? [currentUserId] : [],
      notifyEmailsText: '',
    };
  }

  private patchForm(patch: Partial<ScheduleFormValue>): void {
    this.form = {...this.form, ...patch};
    this.confirmDelete = false;
    this.onFormChanged();
  }

  /** Vorprüfung im Client; nur ein formal vollständiges Formular geht an die Vorschau. */
  private onFormChanged(): void {
    this.formIssues = validateScheduleForm(this.form);
    this.serverError = null;
    if (this.formIssues.length > 0) {
      this.preview = {kind: 'idle'};
      this.nextRunsText = '';
      return;
    }
    this.preview = {kind: 'loading'};
    this.previewTrigger.next(toScheduleRequest(this.form) as CheckRunScheduleRequest);
  }

  /** Fehler der Vorschau werden als Zustand angezeigt, nicht verschluckt. */
  private loadPreview(request: CheckRunScheduleRequest): Observable<PreviewState> {
    return this.api.preview(this.projectId, request).pipe(
      map((preview): PreviewState => ({kind: 'ready', preview})),
      catchError(err => of<PreviewState>({
        kind: 'error',
        message: serverErrorMessage(err, 'Die Vorschau konnte nicht geladen werden.'),
      })),
    );
  }

  private applyPreview(state: PreviewState): void {
    // Hat sich das Formular inzwischen als unvollständig erwiesen, ist die Antwort überholt.
    if (this.formIssues.length > 0) return;
    this.preview = state;
    this.nextRunsText = state.kind === 'ready'
      ? (state.preview.nextRuns ?? []).map(run => formatBerlin(run)).join(' · ')
      : '';
  }

  private loadMembers(): void {
    this.membersLoading = true;
    this.membersError = false;
    this.projectApi.getUsers(this.projectId).subscribe({
      next: users => {
        this.memberOptions = (users ?? [])
          .filter((user): user is UserSimpleDto & { id: number } => user.id != null)
          .map(user => ({id: user.id, label: memberLabel(user)}))
          .sort((a, b) => a.label.localeCompare(b.label, 'de'));
        this.membersLoading = false;
        this.dropNonMemberRecipients();
      },
      error: () => {
        this.memberOptions = [];
        this.membersLoading = false;
        this.membersError = true;
      },
    });
  }

  /**
   * Entfernt Empfänger, die keine Projektmitglieder (mehr) sind. Das Backend würde sie beim
   * Speichern und in der Vorschau ablehnen, und das Select kann sie nicht anzeigen.
   *
   * <p>Nur nach erfolgreichem Laden der Mitglieder aufrufen: nach einem Ladefehler ist
   * `memberOptions` leer, dann bleiben die Empfänger unverändert.
   */
  private dropNonMemberRecipients(): void {
    const {kept, removedCount} = retainMemberRecipients(
      this.form.notifyUserIds,
      this.memberOptions.map(member => member.id),
    );
    if (removedCount === 0) return;
    this.removedRecipientsHint = removedRecipientsHint(removedCount);
    this.patchForm({notifyUserIds: kept});
  }

  private updateInteractiveOption(): void {
    const option = this.fixedDefinitionName === null
      ? this.checkOptions.find(o => o.id === this.form.definitionId)
      : undefined;
    this.interactiveOption = option && option.startSelectionStep != null ? option : null;
  }

  private weekdayOf(isoDate: string): ScheduleWeekdayValue {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(isoDate) ? new Date(`${isoDate}T12:00:00`) : new Date();
    // getDay: 0 = Sonntag; WEEKDAY_OPTIONS beginnt mit Montag.
    return WEEKDAY_OPTIONS[(date.getDay() + 6) % 7].value;
  }
}

/** Wie `getMemberLabel` im Builder-Schritt „Bericht“. */
function memberLabel(user: UserSimpleDto): string {
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || user.mail || `Nutzer ${user.id}`;
}
